import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, Calendar, Globe, DollarSign, Check, ArrowRight,
  Target, TrendingUp, Camera, Users, Award, Clock, Heart,
  ChevronDown, ChevronUp, Dumbbell, Scale, Activity, AlertTriangle,
  Instagram, Share2, CheckCircle, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

export default function Challenge() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);

  const prizes = [
    { place: '1st Place', amount: 'USD $1,250', color: 'from-yellow-400 to-amber-500', icon: '🥇', bg: 'bg-yellow-400/10 border-yellow-400/30' },
    { place: '2nd Place', amount: 'USD $500', color: 'from-zinc-300 to-zinc-400', icon: '🥈', bg: 'bg-zinc-400/10 border-zinc-400/30' },
    { place: '3rd Place', amount: 'USD $250', color: 'from-amber-600 to-amber-700', icon: '🥉', bg: 'bg-amber-600/10 border-amber-600/30' },
  ];

  const eligibility = [
    'Must be 18 years of age or older',
    'Must have an active Vyta-Fit subscription',
    'Must complete onboarding details on vyta-fit.com',
    'Must follow your personalised Vyta-Fit fitness and nutrition plan',
    'Must log all required progress data in your Vyta-Fit dashboard',
    'Must post weekly public progress updates on social media',
  ];

  const howToEnter = [
    { step: '1', title: 'Subscribe', desc: 'Subscribe at vyta-fit.com' },
    { step: '2', title: 'Complete Onboarding', desc: 'Complete your onboarding details (goals, body stats, preferences, lifestyle, allergies, etc.)' },
    { step: '3', title: 'Get Your Plan', desc: 'Receive your personalised Vyta-Fit training and nutrition plan' },
    { step: '4', title: 'Log Progress', desc: 'Start logging your weight, measurements, performance improvements, workout adherence, and nutrition adherence' },
    { step: '5', title: 'Post Weekly', desc: 'Publish one (1) public post per week showing your journey and progress' },
  ];

  const judgingCriteria = [
    { title: 'Plan Adherence', desc: 'Consistency with workouts and nutrition plans, weekly logs', icon: Target },
    { title: 'Physical Transformation', desc: 'Changes in weight or body composition, visual progress', icon: Scale },
    { title: 'Performance Improvements', desc: 'Strength, endurance, mobility, conditioning, sport metrics', icon: Activity },
    { title: 'Engagement & Visibility', desc: 'Weekly posts, tagging vyta-fit.com or @vytafit, storytelling, community impact', icon: Camera },
    { title: 'Overall Impact', desc: 'Sustainability, mindset change, lifestyle improvements, inspirational narrative', icon: Heart },
  ];

  const postingRules = [
    'Participants must post at least one (1) public post per week',
    'Each post must show real progress (photos, videos, training clips, or updates)',
    'Each post must mention vyta-fit.com or tag @vytafit',
    'Each post must use the hashtag #VytaFitChallenge',
    'Posts must be publicly visible',
    'Private accounts must switch to public during the challenge',
  ];

  const rulesConditions = [
    'Participants must maintain an active Vyta-Fit subscription for the full challenge period',
    'All logs must be completed honestly and accurately',
    'Falsifying data or using edited or manipulated images will result in disqualification',
    'Extreme dieting, unsafe training, or unhealthy practices are not encouraged and may result in removal',
    'Vyta-Fit reserves the right to verify logs, posts, and transformation claims',
    'Vyta-Fit\'s decision is final',
    'Prizes will be paid via PayPal, Wise, or bank transfer',
    'Taxes, duties, or fees are the responsibility of the winner',
    'Participants grant Vyta-Fit permission to share submitted content, images, videos, and testimonials for marketing and promotional purposes',
    'Vyta-Fit reserves the right to modify or cancel the challenge at any time if required',
  ];

  return (
    <>
      <SEO 
        title="5-Week Global Transformation Challenge - Win USD $2,000"
        description="Join the Vyta-Fit Global Transformation Challenge! 5 weeks to transform your body and win a share of USD $2,000. Starts January 23, 2025."
        keywords="fitness challenge, transformation challenge, win money fitness, 5 week challenge, fitness competition, body transformation, vyta-fit"
        url="https://vyta-fit.com/challenge"
      />
      <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black text-lg">V</span>
              </div>
              <span className="font-bold text-lg">VYTA</span>
            </Link>
            <Button
              onClick={() => navigate('/register?challenge=true')}
              className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:from-yellow-300 hover:to-amber-400 font-bold rounded-full px-6"
              data-testid="header-join-challenge-btn"
            >
              Join the Challenge
            </Button>
          </div>
        </header>

        {/* HERO SECTION */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-yellow-400/10 to-amber-500/10 blur-[120px]" />
          </div>
          
          <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400/20 to-amber-500/20 border border-yellow-400/30 mb-6">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-semibold">Global Transformation Challenge</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight">
                Vyta-Fit Global
                <br />
                <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                  Transformation Challenge
                </span>
              </h1>
              
              <p className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-6">
                Win a Share of USD $2,000
              </p>
              
              <p className="text-lg text-zinc-400 mb-8 max-w-3xl mx-auto leading-relaxed">
                Join our 5-week global fitness and lifestyle challenge and transform your body, performance, and mindset using your personalised Vyta-Fit fitness and nutrition plan.
              </p>
              
              {/* Key Dates */}
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700">
                  <Calendar className="w-4 h-4 text-[#CCFF00]" />
                  <span className="text-sm"><strong>Start:</strong> Fri 23 Jan, 12:00 AM (NY)</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700">
                  <Calendar className="w-4 h-4 text-[#CCFF00]" />
                  <span className="text-sm"><strong>End:</strong> Fri 27 Feb, 11:59 PM (NY)</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700">
                  <Clock className="w-4 h-4 text-[#CCFF00]" />
                  <span className="text-sm"><strong>Duration:</strong> 5 Weeks</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700">
                  <Globe className="w-4 h-4 text-[#CCFF00]" />
                  <span className="text-sm"><strong>Open Worldwide</strong></span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/register?challenge=true')}
                className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:from-yellow-300 hover:to-amber-400 font-bold rounded-full px-10 py-6 text-lg shadow-lg shadow-yellow-500/20"
                data-testid="hero-join-challenge-btn"
              >
                <Trophy className="w-5 h-5 mr-2" />
                Join the Challenge
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* THE CHALLENGE */}
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Dumbbell className="w-8 h-8 text-[#CCFF00]" />
                The Challenge
              </h2>
              <div className="space-y-4 text-lg text-zinc-300 leading-relaxed">
                <p>
                  The Vyta-Fit Global Transformation Challenge is a 5-week fitness and lifestyle challenge designed to reward <strong className="text-white">real, sustainable results</strong> — not crash dieting or unhealthy extremes.
                </p>
                <p>
                  Participants follow their personalised Vyta-Fit fitness and nutrition plans, log their progress, and share their journey publicly for a chance to win a share of <strong className="text-yellow-400">USD $2,000</strong> in prize money.
                </p>
                <p>
                  Each week, Vyta-Fit will also feature selected participant success stories across our website and social media channels to showcase real journeys, real results, and inspire others.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* PRIZES */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-2 flex items-center justify-center gap-3">
                <DollarSign className="w-8 h-8 text-yellow-400" />
                Prizes
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-3 gap-6">
              {prizes.map((prize, i) => (
                <motion.div
                  key={prize.place}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative p-8 rounded-2xl border ${prize.bg} text-center`}
                >
                  <span className="text-6xl mb-4 block">{prize.icon}</span>
                  <h3 className="text-xl font-bold mb-2">{prize.place}</h3>
                  <p className={`text-3xl font-black bg-gradient-to-r ${prize.color} bg-clip-text text-transparent`}>
                    {prize.amount}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ELIGIBILITY */}
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-[#CCFF00]" />
                Eligibility
              </h2>
              <div className="p-6 rounded-2xl bg-zinc-800/30 border border-zinc-700">
                <ul className="space-y-3">
                  {eligibility.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* HOW TO ENTER */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-2">How to Enter</h2>
            </motion.div>

            <div className="space-y-4">
              {howToEnter.map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-4 p-5 rounded-xl bg-zinc-800/30 border border-zinc-700"
                >
                  <div className="w-10 h-10 rounded-full bg-[#CCFF00] text-black font-bold flex items-center justify-center flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-zinc-400">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 text-center"
            >
              <Button
                onClick={() => navigate('/register?challenge=true')}
                className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:from-yellow-300 hover:to-amber-400 font-bold rounded-full px-8 py-6 text-lg"
                data-testid="enter-now-btn"
              >
                Enter Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* JUDGING CRITERIA */}
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <Award className="w-8 h-8 text-yellow-400" />
                How Winners Are Chosen
              </h2>
              <p className="text-lg text-zinc-400 mb-8">
                Winners will be selected based on a <strong className="text-white">holistic transformation score</strong> — not just weight loss. Each entry will be assessed across:
              </p>

              <div className="space-y-4">
                {judgingCriteria.map((criteria, i) => (
                  <motion.div
                    key={criteria.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="flex gap-4 p-5 rounded-xl bg-zinc-800/30 border border-zinc-700"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#CCFF00]/10 flex items-center justify-center flex-shrink-0">
                      <criteria.icon className="w-6 h-6 text-[#CCFF00]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">{criteria.title}</h3>
                      <p className="text-zinc-400">{criteria.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* WEEKLY POSTING RULES */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <Instagram className="w-8 h-8 text-[#CCFF00]" />
                Weekly Posting Rules
              </h2>
              <div className="p-6 rounded-2xl bg-zinc-800/30 border border-zinc-700">
                <ul className="space-y-3">
                  {postingRules.map((rule, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">{rule}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-6 border-t border-zinc-700 flex flex-wrap gap-3">
                  <span className="px-4 py-2 rounded-full bg-zinc-800 border border-zinc-600 text-sm font-medium">@vytafit</span>
                  <span className="px-4 py-2 rounded-full bg-zinc-800 border border-zinc-600 text-sm font-medium">vyta-fit.com</span>
                  <span className="px-4 py-2 rounded-full bg-[#CCFF00]/20 border border-[#CCFF00]/30 text-[#CCFF00] text-sm font-medium">#VytaFitChallenge</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* LATE ENTRY POLICY */}
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-[#CCFF00]" />
                Late Entries
              </h2>
              <div className="p-6 rounded-2xl bg-zinc-800/30 border border-zinc-700 space-y-4">
                <p className="text-lg text-zinc-300">
                  Entries remain open until <strong className="text-yellow-400">Friday 6 February, 11:59 PM (New York Time)</strong>.
                </p>
                <div>
                  <p className="font-bold text-white mb-2">Late entrants must:</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-zinc-300">
                      <Check className="w-4 h-4 text-[#CCFF00] mt-1 flex-shrink-0" />
                      Complete onboarding within 24 hours of joining
                    </li>
                    <li className="flex items-start gap-2 text-zinc-300">
                      <Check className="w-4 h-4 text-[#CCFF00] mt-1 flex-shrink-0" />
                      Log baseline weight and progress photos
                    </li>
                    <li className="flex items-start gap-2 text-zinc-300">
                      <Check className="w-4 h-4 text-[#CCFF00] mt-1 flex-shrink-0" />
                      Post their first public progress update within 72 hours
                    </li>
                  </ul>
                </div>
                <p className="text-zinc-400">
                  All participants, including late entrants, will finish the challenge on <strong className="text-white">Friday 27 February</strong>.
                </p>
                <p className="text-zinc-400">
                  Late entrants will be judged based on: rate of transformation, plan adherence, performance improvements, consistency, and overall impact.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CHALLENGE PERIOD */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-[#CCFF00]" />
                Challenge Period
              </h2>
              <div className="p-6 rounded-2xl bg-gradient-to-r from-[#CCFF00]/10 to-yellow-400/10 border border-[#CCFF00]/30 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-zinc-900/50">
                    <p className="text-sm text-zinc-400 mb-1">Start</p>
                    <p className="text-lg font-bold">Friday 23 January, 12:00 AM (NY)</p>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-900/50">
                    <p className="text-sm text-zinc-400 mb-1">End</p>
                    <p className="text-lg font-bold">Friday 27 February, 11:59 PM (NY)</p>
                  </div>
                </div>
                <ul className="space-y-2 text-zinc-300">
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                    Final judging will occur within 7 days of the challenge end date
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                    Winners will be announced on vyta-fit.com and Vyta-Fit social channels
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                    Weekly participant success stories will be published by Vyta-Fit during the challenge
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* RULES & CONDITIONS */}
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
                Rules & Conditions
              </h2>
              <div className="p-6 rounded-2xl bg-zinc-800/30 border border-zinc-700">
                <ul className="space-y-3">
                  {rulesConditions.map((rule, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                        {i + 1}
                      </span>
                      <span className="text-zinc-300 text-sm">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* HEALTH DISCLAIMER */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-xl bg-zinc-800/50 border border-zinc-700"
            >
              <h3 className="font-bold text-zinc-400 mb-2 text-sm uppercase tracking-wider">
                Health & Safety Disclaimer
              </h3>
              <p className="text-sm text-zinc-500">
                Vyta-Fit provides personalised fitness and nutrition plans for general wellness purposes only. Participants should consult a qualified healthcare professional before beginning any fitness or nutrition program. Vyta-Fit is not responsible for injuries or adverse health outcomes.
              </p>
            </motion.div>
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Ready to Transform?
              </h2>
              <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
                Join thousands of others transforming their bodies, performance, and mindset with Vyta-Fit.
              </p>
              <Button
                onClick={() => navigate('/register?challenge=true')}
                className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:from-yellow-300 hover:to-amber-400 font-bold rounded-full px-10 py-6 text-lg shadow-lg shadow-yellow-500/20"
                data-testid="footer-join-challenge-btn"
              >
                <Trophy className="w-5 h-5 mr-2" />
                Join the Challenge
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black">V</span>
              </div>
              <span className="font-bold">VYTA</span>
            </Link>
            <p className="text-zinc-500 text-sm">
              © {new Date().getFullYear()} VYTA. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
